import React from "react";
import { useForm } from "react-hook-form";

const ClinicInfoForm = ({ clinicInfo, logoDimensions, onSubmit, handleLogoChange }) => {
    const { register, handleSubmit, formState: { errors }, setValue } = useForm({
        defaultValues: clinicInfo || {},
    });

    const VerificationWarning = ({ isVerified, fieldName }) => {
        return (
            <>
                {isVerified ? (
                    <span className="text-success ms-2" title={`${fieldName} verified`}>
                    <i className="bi bi-check-circle-fill"></i>
                </span>
                ) : (
                    <span className="text-warning ms-2" title={`${fieldName} not verified`}>
                    <i className="bi bi-exclamation-triangle-fill"></i>
                </span>
                )}
            </>
        );
    };

    const formatPhoneNumber = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 6) return `(${numbers.slice(0,3)}) ${numbers.slice(3)}`;
        return `(${numbers.slice(0,3)}) ${numbers.slice(3,6)}-${numbers.slice(6,10)}`;
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        setValue(e.target.name, formatted);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="row g-4">

            {/* Basic Info */}
            <div className="col-md-8">
                <div className="card">
                    <div className="card-body">
                        <h4 className="card-title mb-3">
                            <i className="bi bi-info-circle me-2"></i> Basic Information
                        </h4>
                        <table className="table table-striped">
                            <tbody>
                            <tr>
                                <th scope="row" className="w-25">Clinic Name</th>
                                <td>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.name ? "is-invalid" : ""}`}
                                        {...register("name", { required: "Clinic name is required" })}
                                    />
                                    {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                                </td>
                            </tr>
                            <tr>
                                <th scope="row">Number</th>
                                <td>
                                    <input type="text" className="form-control" disabled {...register("number")} />
                                </td>
                            </tr>
                            <tr>
                                <th scope="row">Type</th>
                                <td>
                                    <input type="text" className="form-control" disabled {...register("clinicType")} />
                                </td>
                            </tr>
                            <tr>
                                <th scope="row">Started</th>
                                <td>
                                    <input type="text" className="form-control" disabled {...register("dateStarted")} />
                                </td>
                            </tr>
                            <tr>
                                <th scope="row">Clinic Signature</th>
                                <td>
                                    <input type="text" className="form-control"  {...register("signature")} />
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Logo */}
            <div className="col-md-4">
                {clinicInfo?.status === "ON" ? (
                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-body p-4">
                            <div className="row align-items-center">
                                <div className="col-auto">
                                    <div className="bg-success bg-opacity-10 p-3 rounded-circle">
                                        <i className="bi bi-check-circle-fill text-success fs-2"></i>
                                    </div>
                                </div>
                                <div className="col">
                                    <h4 className="mb-1 text-success fw-bold">Clinic Status: Active</h4>
                                    <p className="text-muted mb-0">Your Eyeconx service is functional.</p>
                                </div>

                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-body p-4">
                            <div className="row align-items-center">
                                <div className="col-auto">
                                    <div className="bg-danger bg-opacity-10 p-3 rounded-circle">
                                        <i className="bi bi-x-circle-fill text-danger fs-2"></i>
                                    </div>
                                </div>
                                <div className="col">
                                    <h4 className="mb-1 text-danger fw-bold">Clinic Status: Inactive</h4>
                                    <p className="text-muted mb-0">Your service is stopped.</p>
                                </div>

                            </div>
                        </div>
                    </div>
                )}
                <div className="card bg-light">
                    <div className="card-body">
                        <h6 className="card-title"><i className="bi bi-image me-2"></i> Clinic Logo</h6>
                        {clinicInfo.logo && (
                            <div className="d-flex flex-column gap-2 align-items-center mb-3">
                                <img
                                    src={clinicInfo.logo}
                                    alt="Clinic Logo"
                                    className="img-thumbnail"
                                    style={{ objectFit: "contain" }}
                                />

                            </div>
                        )}
                        <input
                            type="url"
                            className="form-control"
                            {...register("logo")}
                            onBlur={(e) => handleLogoChange(e.target.value)}
                            placeholder="Logo URL"
                        />
                    </div>
                </div>
            </div>

            {/* Contact Info */}
            <div className="col-md-12">
                <div className="card">
                    <div className="card-body">
                        <h4 className="card-title mb-3"><i className="bi bi-telephone me-2"></i> Contact Information</h4>
                        <div className="row">
                            <div className="col-md-8">
                                <table className="table table-striped">
                                    <tbody>
                                    <tr>
                                        <th scope="row" className="w-25">Email</th>
                                        <td className="d-flex align-items-center">
                                            <input
                                                type="email"
                                                className={`form-control ${errors.email ? "is-invalid" : ""}`}
                                                {...register("email", { required: "Email required", pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Invalid email" } })}
                                            />
                                            <VerificationWarning isVerified={clinicInfo.verification?.email} fieldName="Email" />
                                            {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope="row">Phone</th>
                                        <td className="d-flex align-items-center">
                                            <input
                                                type="tel"
                                                className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                                                {...register("phone", { required: "Phone required", validate: v => v.replace(/\D/g,'').length === 10 || "Must be 10 digits" })}
                                                onChange={handlePhoneChange}
                                                placeholder="(###) ###-####"
                                            />
                                            <VerificationWarning isVerified={clinicInfo.verification?.phone} fieldName="Phone" />
                                            {errors.phone && <div className="invalid-feedback">{errors.phone.message}</div>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope="row">SMS</th>
                                        <td className="d-flex align-items-center">
                                            <input
                                                type="tel"
                                                className={`form-control ${errors.sms ? "is-invalid" : ""}`}
                                                disabled
                                                {...register("sms", { required: "SMS required", validate: v => v.replace(/\D/g,'').length === 10 || "Must be 10 digits" })}
                                                onChange={handlePhoneChange}
                                                placeholder="(###) ###-####"
                                            />
                                            <VerificationWarning isVerified={clinicInfo.verification?.sms} fieldName="SMS" />
                                            {errors.sms && <div className="invalid-feedback">{errors.sms.message}</div>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope="row">Website</th>
                                        <td>
                                            <input type="url" className="form-control" {...register("website")} />
                                        </td>
                                    </tr>

                                    </tbody>
                                </table>
                            </div>

                            <div className="col-md-4">
                                {clinicInfo.verification?.email && clinicInfo.verification?.phone && clinicInfo.verification?.sms ? (
                                    <div className="card bg-success bg-opacity-50 mb-3">
                                        <div className="card-body d-flex align-items-start">
                                            <i className="bi bi-check-circle-fill text-dark fs-1 me-3"></i>
                                            <div>
                                                <h6 className="fw-bold mb-1">Verified Records</h6>
                                                <p className="mb-0 small">Email, SMS, and Phone are all verified.</p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="card bg-warning bg-opacity-50 mb-3">
                                        <div className="card-body d-flex align-items-start">
                                            <i className="bi bi-exclamation-circle-fill text-dark fs-1 me-3"></i>
                                            <div>
                                                <h6 className="fw-bold mb-1">Verification Required</h6>
                                                <p className="mb-0 small">
                                                    Verify your <strong>email, SMS</strong>, and <strong>phone numbers</strong> to ensure you receive the most accurate information.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Address */}
            <div className="col-md-8">
                <div className="card">
                    <div className="card-body">
                        <h4 className="card-title mb-3"><i className="bi bi-geo-alt me-2"></i> Address</h4>
                        <address>
                            <input type="text" className="form-control mb-2" {...register("street")} placeholder="Street" />
                            <input type="text" className="form-control mb-2" {...register("city")} placeholder="City" />
                            <input type="text" className="form-control mb-2" {...register("province")} placeholder="Province" />
                            <input type="text" className="form-control mb-2" {...register("postalCode")} placeholder="Postal Code" />
                            <input type="text" className="form-control mb-2" {...register("country")} placeholder="Country" />
                        </address>
                    </div>
                </div>
            </div>

            <div className="col-md-4">
                <div className="card bg-light">
                    <div className="card-body">
                        <h6 className="card-title"><i className="bi bi-clock me-2"></i> Timezone</h6>
                        <span className="fw-bold fs-5">{clinicInfo?.timezone?.timezoneName}</span>
                    </div>
                </div>
            </div>

            <div className="col-12 d-flex justify-content-end">
                <button type="submit" className="btn btn-primary text-white">
                    <i className="bi bi-check-lg me-1"></i> Save Changes
                </button>
            </div>

        </form>
    );
};

export default ClinicInfoForm;
